import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SectionHead, Reveal } from "@/components/Reveal";
import { wa, WA_MESSAGES } from "@/components/WhatsApp";

const PATHS = [
  {
    title: "Personalized Training",
    tagline: "Built around you.",
    desc: "One-to-one training designed around your assessment, goals, movement capabilities and progress.",
    features: ["Individual Capability Assessment", "Personalized Training Plan", "One-to-One Coaching",
      "Progress Tracking", "Regular Reassessments", "Workout History", "Coach Feedback"],
    cta: "Get my personalized plan",
    href: wa(WA_MESSAGES.personalized),
    external: true,
    image: "/images/uploads/stretch.jpg",
    alt: "One-to-one coaching — guided mobility work in the studio",
  },
  {
    title: "Group Sessions",
    tagline: "Train together. Progress together.",
    desc: "Structured Capable 360 sessions combining purposeful training, coaching and the energy of a group.",
    features: ["Coach-Led Sessions", "Small Group Training", "Structured Workouts",
      "Six Capability Training System", "Progressive Sessions", "Individual Progress Tracking"],
    cta: "Join a group session",
    href: "/sessions",
    external: false,
    image: "/images/uploads/partners.jpg",
    alt: "Training partners pushing through a partner workout",
  },
];

export default function ChooseTrain() {
  return (
    <section data-testid="choose-train-section" className="bg-ink py-24 sm:py-32 px-5 sm:px-10 lg:px-16">
      <SectionHead overline="Choose how you train" className="mb-4">
        Different goals. Different journeys.
        <span className="block text-stroke">One approach — becoming more capable.</span>
      </SectionHead>
      <div className="grid md:grid-cols-2 gap-4 mt-12">
        {PATHS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08}>
            <article data-testid={`path-card-${i}`}
              className="group border border-zinc-800 bg-zinc-950 overflow-hidden hover:border-volt/60 transition-colors duration-500 h-full flex flex-col">
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={p.image} alt={p.alt} loading="lazy"
                  className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent" />
              </div>
              <div className="p-7 sm:p-9 flex flex-col flex-1">
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-volt">{p.title}</p>
                <h3 className="font-display uppercase text-4xl sm:text-5xl leading-[0.9] mt-2">{p.tagline}</h3>
                <p className="text-sm text-zinc-400 mt-4 leading-relaxed">{p.desc}</p>
                <ul className="mt-5 space-y-2 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-zinc-300">
                      <span className="w-1.5 h-1.5 bg-volt rotate-45 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                {p.external ? (
                  <a href={p.href} target="_blank" rel="noopener noreferrer" data-testid={`path-cta-${i}`}
                    className="group/btn mt-7 inline-flex items-center gap-3 border border-volt text-volt uppercase tracking-[0.16em] text-xs font-extrabold px-7 py-4 hover:bg-volt hover:text-black transition-colors self-start">
                    {p.cta} <ArrowRight size={15} className="transition-transform group-hover/btn:translate-x-1" />
                  </a>
                ) : (
                  <Link to={p.href} data-testid={`path-cta-${i}`}
                    className="group/btn mt-7 inline-flex items-center gap-3 border border-volt text-volt uppercase tracking-[0.16em] text-xs font-extrabold px-7 py-4 hover:bg-volt hover:text-black transition-colors self-start">
                    {p.cta} <ArrowRight size={15} className="transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                )}
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
